import { useState } from "react";
import { authFetch } from "../api";
import { Link } from "react-router-dom";

// --- Helper Components ---

function TierBadge({ tier }) {
  if (!tier) return <span style={{ color: "#ccc" }}>-</span>;

  const tierColors = {
    "Walk up": { bg: "#f5f5f5", color: "#666" },
    "Walk up plus": { bg: "#efebe9", color: "#5d4037" },
    "E only": { bg: "#e3f2fd", color: "#1565c0" },
    "E plus": { bg: "#bbdefb", color: "#0d47a1" },
    "E/D only": { bg: "#e8f5e9", color: "#2e7d32" },
    "Low": { bg: "#fff3e0", color: "#e65100" },
    "Med": { bg: "#fce4ec", color: "#c2185b" },
    "High": { bg: "#f3e5f5", color: "#7b1fa2" },
    "Luxury": { bg: "#fff8e1", color: "#f57f17" },
  };

  const style = tierColors[tier] || { bg: "#f5f5f5", color: "#666" };

  return (
    <span style={{
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "10px",
      fontWeight: "500",
      backgroundColor: style.bg,
      color: style.color,
      whiteSpace: "nowrap",
    }}>
      {tier}
    </span>
  );
}

function AmenityBadge({ label, value, detail }) {
  if (!value) return null;
  return (
    <span style={amenityStyle}>
      {label}
      {detail && <span style={{ fontSize: "10px", marginLeft: "4px" }}>({detail})</span>}
    </span>
  );
}

function AmenityToggle({ label, checked, onChange }) {
  return (
    <label style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      cursor: "pointer",
      padding: "4px 8px",
      borderRadius: "4px",
      backgroundColor: checked ? "#e3f2fd" : "#f5f5f5",
      border: `1px solid ${checked ? "#1976d2" : "#ddd"}`,
      fontSize: "12px",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ cursor: "pointer" }}
      />
      <span style={{ color: checked ? "#1976d2" : "#666" }}>{label}</span>
    </label>
  );
}

const boroughName = (borough) => {
  const names = {
    1: "Manhattan",
    2: "Bronx",
    3: "Brooklyn",
    4: "Queens",
    5: "Staten Island",
  };
  return names[borough] || "-";
};

// --- Styles ---

const panelOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "flex-end",
  zIndex: 1000,
};

const panelStyle = {
  width: "650px",
  maxWidth: "90vw",
  height: "100%",
  backgroundColor: "#fff",
  padding: "24px",
  overflowY: "auto",
  boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.1)",
};

const sectionHeaderStyle = {
  fontSize: "16px",
  fontWeight: "600",
  marginBottom: "12px",
  color: "#333",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px",
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const labelStyle = {
  fontSize: "12px",
  color: "#666",
  fontWeight: "500",
};

const amenityStyle = {
  padding: "6px 12px",
  backgroundColor: "#e3f2fd",
  borderRadius: "16px",
  fontSize: "12px",
  color: "#1565c0",
};

const editInputStyle = {
  padding: "6px 10px",
  border: "1px solid #ddd",
  borderRadius: "4px",
  fontSize: "14px",
  width: "100%",
  boxSizing: "border-box",
};

// --- Main Component ---

export default function BuildingDetailPanel({
  building,
  loading,
  onClose,
  onFeedbackAdded,
}) {
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  // SE enrichment state
  const [seUrlInput, setSeUrlInput] = useState("");
  const [fetchingSe, setFetchingSe] = useState(false);
  const [seError, setSeError] = useState("");
  const [showUrlOverride, setShowUrlOverride] = useState(false);
  // Verify state
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    setVerifying(true);
    try {
      await authFetch(`/buildings/${building.id}/verify`, {
        method: "POST",
      });
      onFeedbackAdded();
    } catch (error) {
      console.error("Error toggling verified:", error);
      alert("Failed to update verified status");
    } finally {
      setVerifying(false);
    }
  };

  const startEditing = () => {
    setEditData({
      total_units: building.total_units || "",
      total_floors: building.total_floors || "",
      year_built: building.year_built || "",
      year_renovated: building.year_renovated || "",
      ownership_type: building.ownership_type || "",
      building_type: building.building_type || "",
      building_tier: building.building_tier || "",
      doorman: building.doorman || false,
      elevator_in_building: building.elevator || false,
      concierge: building.concierge || false,
      laundry: building.laundry || false,
      storage: building.storage || false,
      healthclub: building.healthclub || false,
      pool: building.pool || false,
      courtyard: building.courtyard || false,
      roof_deck: building.roof_deck || false,
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
  };

  const saveEditing = async () => {
    setSaving(true);
    try {
      const payload = {};
      const numField = (key, editVal, origVal) => {
        const parsed = editVal === "" || editVal === null ? null : parseInt(editVal);
        if (parsed !== (origVal || null)) payload[key] = parsed;
      };
      numField("total_units", editData.total_units, building.total_units);
      numField("total_floors", editData.total_floors, building.total_floors);
      numField("year_built", editData.year_built, building.year_built);
      numField("year_renovated", editData.year_renovated, building.year_renovated);
      if (editData.ownership_type !== (building.ownership_type || "")) {
        payload.ownership_type = editData.ownership_type || null;
      }
      if (editData.building_type !== (building.building_type || "")) {
        payload.building_type = editData.building_type || null;
      }
      if (editData.building_tier !== (building.building_tier || "")) {
        payload.building_tier = editData.building_tier || null;
      }

      if (editData.doorman !== (building.doorman || false)) payload.doorman = editData.doorman;
      if (editData.elevator_in_building !== (building.elevator || false)) payload.elevator_in_building = editData.elevator_in_building;
      if (editData.concierge !== (building.concierge || false)) payload.concierge = editData.concierge;
      if (editData.laundry !== (building.laundry || false)) payload.laundry = editData.laundry;
      if (editData.storage !== (building.storage || false)) payload.storage = editData.storage;
      if (editData.healthclub !== (building.healthclub || false)) payload.healthclub = editData.healthclub;
      if (editData.pool !== (building.pool || false)) payload.pool = editData.pool;
      if (editData.courtyard !== (building.courtyard || false)) payload.courtyard = editData.courtyard;
      if (editData.roof_deck !== (building.roof_deck || false)) payload.roof_deck = editData.roof_deck;

      if (Object.keys(payload).length > 0) {
        await authFetch(`/buildings/${building.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        onFeedbackAdded();
      }
      setIsEditing(false);
      setEditData({});
    } catch (error) {
      console.error("Error saving building:", error);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;

    setSubmitting(true);
    try {
      await authFetch(`/buildings/${building.id}/notes`, {
        method: "POST",
        body: JSON.stringify({ note: noteText }),
      });
      setNoteText("");
      onFeedbackAdded();
    } catch (error) {
      console.error("Error adding note:", error);
      alert("Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;

    try {
      await authFetch(`/buildings/${building.id}/notes/${noteId}`, {
        method: "DELETE",
      });
      onFeedbackAdded();
    } catch (error) {
      console.error("Error deleting note:", error);
      alert("Failed to delete note");
    }
  };

  const handleFetchSeAmenities = async (urlToUse) => {
    if (!urlToUse) {
      setSeError("Please enter a StreetEasy URL");
      return;
    }
    setFetchingSe(true);
    setSeError("");
    try {
      const result = await authFetch(`/buildings/${building.id}/fetch-se-amenities`, {
        method: "POST",
        body: JSON.stringify({ url: urlToUse }),
      });
      if (result.detail) {
        setSeError(result.detail + " Try a different StreetEasy URL.");
        return;
      }
      setSeUrlInput("");
      setShowUrlOverride(false);
      onFeedbackAdded();
    } catch (error) {
      console.error("Error fetching SE amenities:", error);
      setSeError(error.message || "Failed to fetch SE amenities. Try a different URL.");
    } finally {
      setFetchingSe(false);
    }
  };

  if (loading) {
    return (
      <div style={panelOverlayStyle}>
        <div style={panelStyle}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading building details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={panelOverlayStyle}>
      <div style={panelStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>{building.display_address}</h2>
            {building.building_name && (
              <p style={{ margin: "4px 0 0", color: "#666" }}>{building.building_name}</p>
            )}
            {building.complex_name && (
              <p style={{ margin: "4px 0 0", color: "#1976d2", fontSize: "13px" }}>Complex: {building.complex_name}</p>
            )}
            {/* Search Links */}
            <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(building.full_address || building.display_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "12px",
                  color: "#1976d2",
                  textDecoration: "none",
                  padding: "4px 8px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                Google
              </a>
              <a
                href={`https://streeteasy.com/search?utf8=%E2%9C%93&search=${encodeURIComponent(building.full_address || building.display_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: "12px",
                  color: "#e65100",
                  textDecoration: "none",
                  padding: "4px 8px",
                  backgroundColor: "#fff3e0",
                  borderRadius: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                StreetEasy
              </a>
              <button
                onClick={handleVerify}
                disabled={verifying}
                style={{
                  fontSize: "12px",
                  color: building.verified ? "#2e7d32" : "#666",
                  textDecoration: "none",
                  padding: "4px 8px",
                  backgroundColor: building.verified ? "#e8f5e9" : "#f5f5f5",
                  borderRadius: "4px",
                  border: building.verified ? "1px solid #2e7d32" : "1px solid #ddd",
                  cursor: verifying ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {building.verified ? "Verified" : "Mark Verified"}
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            &times;
          </button>
        </div>

        {/* Location */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Location</h3>
          <div style={gridStyle}>
            <div style={fieldStyle}>
              <span style={labelStyle}>Full Address</span>
              <span>{building.full_address || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Neighborhood</span>
              <span>{building.neighborhood || "-"}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Borough</span>
              <span>{boroughName(building.borough)}</span>
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Postal Code</span>
              <span>{building.postal_code || "-"}</span>
            </div>
            {building.zone_name && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Zone</span>
                <span>{building.zone_name} (Score: {building.zone_score})</span>
              </div>
            )}
            {building.location_score && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Location Score</span>
                <span>{building.location_score.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Building Info */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h3 style={{ ...sectionHeaderStyle, marginBottom: 0 }}>Building Details</h3>
            {!isEditing ? (
              <button
                onClick={startEditing}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
            ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={cancelEditing}
                  disabled={saving}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveEditing}
                  disabled={saving}
                  style={{
                    padding: "6px 12px",
                    fontSize: "12px",
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            )}
          </div>
          <div style={gridStyle}>
            <div style={fieldStyle}>
              <span style={labelStyle}>Total Units</span>
              {isEditing ? (
                <input type="number" value={editData.total_units} onChange={(e) => setEditData({ ...editData, total_units: e.target.value })} style={editInputStyle} />
              ) : (
                <span>{building.total_units || "-"}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Total Floors</span>
              {isEditing ? (
                <input type="number" value={editData.total_floors} onChange={(e) => setEditData({ ...editData, total_floors: e.target.value })} style={editInputStyle} />
              ) : (
                <span>{building.total_floors || "-"}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Year Built</span>
              {isEditing ? (
                <input type="number" value={editData.year_built} onChange={(e) => setEditData({ ...editData, year_built: e.target.value })} style={editInputStyle} />
              ) : (
                <span>{building.year_built || "-"}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Year Renovated</span>
              {isEditing ? (
                <input type="number" value={editData.year_renovated} onChange={(e) => setEditData({ ...editData, year_renovated: e.target.value })} style={editInputStyle} />
              ) : (
                <span>{building.year_renovated || "-"}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Ownership Type</span>
              {isEditing ? (
                <select value={editData.ownership_type} onChange={(e) => setEditData({ ...editData, ownership_type: e.target.value })} style={editInputStyle}>
                  <option value="">-- Select --</option>
                  <option value="CONDO">Condo</option>
                  <option value="COOP">Co-op</option>
                  <option value="CONDP">Condop</option>
                  <option value="RNTL">Rental</option>
                  <option value="MIXEDRENTAL">Mixed-Use Rental</option>
                  <option value="MIXEDCONDO">Mixed-Use Condo</option>
                  <option value="MULTI">Multi Family</option>
                  <option value="TOWNHOUSE">Townhouse</option>
                  <option value="SFAM">Single Family</option>
                  <option value="HOUSE">House</option>
                  <option value="OFFIC">Office</option>
                  <option value="LAND">Land</option>
                </select>
              ) : (
                <span>{building.ownership_type || "-"}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Building Type</span>
              {isEditing ? (
                <select value={editData.building_type} onChange={(e) => setEditData({ ...editData, building_type: e.target.value })} style={editInputStyle}>
                  <option value="">-- Select --</option>
                  <option value="HIRISE">Hi-Rise (16+ stories)</option>
                  <option value="MIDRISE">Mid-Rise (8-16 stories)</option>
                  <option value="LOWRISE">Low-Rise (7 stories)</option>
                  <option value="WALKUP">Walk-up</option>
                  <option value="TOWNHOUSE">Townhouse</option>
                  <option value="HOUSE">House</option>
                  <option value="UNKNOWN">Unknown</option>
                </select>
              ) : (
                <span>{{ HIRISE: "Hi-Rise", MIDRISE: "Mid-Rise", LOWRISE: "Low-Rise", WALKUP: "Walk-up", TOWNHOUSE: "Townhouse", HOUSE: "House", LOFT: "Loft", UNKNOWN: "Unknown" }[building.building_type] || building.building_type || "-"}</span>
              )}
            </div>
            <div style={fieldStyle}>
              <span style={labelStyle}>Building Tier</span>
              {isEditing ? (
                <select value={editData.building_tier} onChange={(e) => setEditData({ ...editData, building_tier: e.target.value })} style={editInputStyle}>
                  <option value="">-- Select --</option>
                  <option value="Walk up">Walk up</option>
                  <option value="Walk up plus">Walk up plus</option>
                  <option value="E only">E only</option>
                  <option value="E plus">E plus</option>
                  <option value="E/D only">E/D only</option>
                  <option value="Low">Low</option>
                  <option value="Med">Med</option>
                  <option value="High">High</option>
                  <option value="Luxury">Luxury</option>
                </select>
              ) : (
                <TierBadge tier={building.building_tier} />
              )}
            </div>
          </div>
        </div>

        {/* Amenities */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Amenities</h3>
          {isEditing ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              <AmenityToggle label="Doorman" checked={editData.doorman} onChange={(v) => setEditData({ ...editData, doorman: v })} />
              <AmenityToggle label="Elevator" checked={editData.elevator_in_building} onChange={(v) => setEditData({ ...editData, elevator_in_building: v })} />
              <AmenityToggle label="Concierge" checked={editData.concierge} onChange={(v) => setEditData({ ...editData, concierge: v })} />
              <AmenityToggle label="Laundry" checked={editData.laundry} onChange={(v) => setEditData({ ...editData, laundry: v })} />
              <AmenityToggle label="Storage" checked={editData.storage} onChange={(v) => setEditData({ ...editData, storage: v })} />
              <AmenityToggle label="Gym" checked={editData.healthclub} onChange={(v) => setEditData({ ...editData, healthclub: v })} />
              <AmenityToggle label="Pool" checked={editData.pool} onChange={(v) => setEditData({ ...editData, pool: v })} />
              <AmenityToggle label="Courtyard" checked={editData.courtyard} onChange={(v) => setEditData({ ...editData, courtyard: v })} />
              <AmenityToggle label="Roof Deck" checked={editData.roof_deck} onChange={(v) => setEditData({ ...editData, roof_deck: v })} />
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              <AmenityBadge label="Doorman" value={building.doorman} detail={building.doorman_coverage} />
              <AmenityBadge label="Concierge" value={building.concierge} />
              <AmenityBadge label="Elevator" value={building.elevator} detail={building.elevators ? `${building.elevators} elevators` : null} />
              <AmenityBadge label="Laundry" value={building.laundry} />
              <AmenityBadge label="Storage" value={building.storage} />
              <AmenityBadge label="Gym" value={building.healthclub} />
              <AmenityBadge label="Pool" value={building.pool} />
              <AmenityBadge label="Courtyard" value={building.courtyard} />
              <AmenityBadge label="Roof Deck" value={building.roof_deck} />
              {building.pets_allowed && (
                <span style={{ ...amenityStyle, backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
                  Pets: {building.pets_allowed}
                </span>
              )}
            </div>
          )}
        </div>

        {/* SE Enrichment Section */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>StreetEasy Enrichment</h3>
          {building.has_se_amenities ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ color: "#2e7d32", fontSize: "14px" }}>Imported from StreetEasy</span>
              </div>
              {building.se_amenities && building.se_amenities.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {building.se_amenities.map((a, idx) => (
                    <span key={idx} style={{ ...amenityStyle, backgroundColor: "#fff3e0", color: "#e65100", fontSize: "11px", padding: "4px 8px" }}>
                      {a}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : building.se_url ? (
            <div>
              <div style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>
                SE URL found: <a href={building.se_url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>{building.se_url}</a>
                <button
                  onClick={() => setShowUrlOverride(!showUrlOverride)}
                  style={{
                    marginLeft: "8px",
                    padding: "2px 8px",
                    backgroundColor: "transparent",
                    color: "#666",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "11px",
                    cursor: "pointer",
                  }}
                >
                  {showUrlOverride ? "Cancel" : "Change URL"}
                </button>
              </div>
              {showUrlOverride ? (
                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                  <input
                    type="text"
                    placeholder="https://streeteasy.com/building/..."
                    value={seUrlInput}
                    onChange={(e) => setSeUrlInput(e.target.value)}
                    style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px" }}
                  />
                  <button
                    onClick={() => handleFetchSeAmenities(seUrlInput)}
                    disabled={fetchingSe || !seUrlInput.trim()}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: fetchingSe || !seUrlInput.trim() ? "#ccc" : "#e65100",
                      color: "#fff", border: "none", borderRadius: "4px", fontSize: "13px",
                      cursor: fetchingSe || !seUrlInput.trim() ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    {fetchingSe ? "Fetching..." : "Fetch"}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleFetchSeAmenities(building.se_url)}
                  disabled={fetchingSe}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: fetchingSe ? "#ccc" : "#e65100",
                    color: "#fff", border: "none", borderRadius: "4px", fontSize: "13px",
                    cursor: fetchingSe ? "not-allowed" : "pointer",
                  }}
                >
                  {fetchingSe ? "Fetching..." : "Fetch SE Amenities"}
                </button>
              )}
              {seError && <div style={{ color: "#d32f2f", fontSize: "12px", marginTop: "8px" }}>{seError}</div>}
            </div>
          ) : (
            <div>
              <div style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>
                No SE URL found. Enter a StreetEasy building/unit URL to fetch amenities:
              </div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  type="text"
                  placeholder="https://streeteasy.com/building/..."
                  value={seUrlInput}
                  onChange={(e) => setSeUrlInput(e.target.value)}
                  style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px" }}
                />
                <button
                  onClick={() => handleFetchSeAmenities(seUrlInput)}
                  disabled={fetchingSe || !seUrlInput.trim()}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: fetchingSe || !seUrlInput.trim() ? "#ccc" : "#e65100",
                    color: "#fff", border: "none", borderRadius: "4px", fontSize: "13px",
                    cursor: fetchingSe || !seUrlInput.trim() ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {fetchingSe ? "Fetching..." : "Fetch"}
                </button>
              </div>
              {seError && <div style={{ color: "#d32f2f", fontSize: "12px", marginTop: "8px" }}>{seError}</div>}
            </div>
          )}
        </div>

        {/* Distance Info */}
        {(building.dist_to_subway || building.dist_to_park) && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={sectionHeaderStyle}>Proximity</h3>
            <div style={gridStyle}>
              {building.dist_to_subway && (
                <div style={fieldStyle}>
                  <span style={labelStyle}>Distance to Subway</span>
                  <span>{(building.dist_to_subway / 1609.34).toFixed(2)} mi ({Math.round(building.dist_to_subway)} m)</span>
                </div>
              )}
              {building.dist_to_park && (
                <div style={fieldStyle}>
                  <span style={labelStyle}>Distance to Park</span>
                  <span>{(building.dist_to_park / 1609.34).toFixed(2)} mi ({Math.round(building.dist_to_park)} m)</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Listings */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>Active Listings</h3>
          <div style={{ display: "flex", gap: "24px" }}>
            {/* For Sale */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#1976d2", marginBottom: "8px" }}>
                For Sale ({building.active_sale_listings})
              </div>
              {building.listings?.filter(l => l.sale_or_rental === "S").length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {building.listings.filter(l => l.sale_or_rental === "S").map(l => (
                    <Link
                      key={l.id}
                      to={`/listings?listing_id=${l.id}`}
                      style={{ fontSize: "13px", color: "#333", padding: "4px 8px", backgroundColor: "#e3f2fd", borderRadius: "4px", textDecoration: "none", display: "block" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {l.unit_number || "No unit"}
                      {l.bedrooms != null && <span style={{ color: "#666", marginLeft: "8px" }}>{l.bedrooms}BR</span>}
                      {l.list_price && <span style={{ color: "#1976d2", marginLeft: "8px" }}>${(l.list_price / 1000000).toFixed(2)}M</span>}
                    </Link>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: "13px", color: "#999" }}>No active sales</div>
              )}
            </div>
            {/* For Rent */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#2e7d32", marginBottom: "8px" }}>
                For Rent ({building.active_rental_listings})
              </div>
              {building.listings?.filter(l => l.sale_or_rental === "R").length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {building.listings.filter(l => l.sale_or_rental === "R").map(l => (
                    <Link
                      key={l.id}
                      to={`/listings?listing_id=${l.id}`}
                      style={{ fontSize: "13px", color: "#333", padding: "4px 8px", backgroundColor: "#e8f5e9", borderRadius: "4px", textDecoration: "none", display: "block" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {l.unit_number || "No unit"}
                      {l.bedrooms != null && <span style={{ color: "#666", marginLeft: "8px" }}>{l.bedrooms}BR</span>}
                      {l.list_price && <span style={{ color: "#2e7d32", marginLeft: "8px" }}>${l.list_price.toLocaleString()}</span>}
                    </Link>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: "13px", color: "#999" }}>No active rentals</div>
              )}
            </div>
          </div>
        </div>

        {/* Building Notes Section */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={sectionHeaderStyle}>
            Building Notes ({building.feedback?.length || 0})
          </h3>
          <p style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
            These notes are used by the AI agent when predicting rent for units in this building.
          </p>

          {/* Add Note Form */}
          <form onSubmit={handleAddNote} style={{ marginBottom: "16px" }}>
            <div style={{ marginBottom: "12px" }}>
              <textarea
                placeholder="Add a note about this building (e.g., 'Building has excellent roof deck with Manhattan views', 'Lobby recently renovated')..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  minHeight: "80px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={!noteText.trim() || submitting}
              style={{
                padding: "10px 20px",
                backgroundColor: noteText.trim() ? "#1976d2" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: noteText.trim() ? "pointer" : "not-allowed",
              }}
            >
              {submitting ? "Adding..." : "Add Note"}
            </button>
          </form>

          {/* Existing Notes */}
          {building.feedback?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {building.feedback.map((note) => (
                <div
                  key={note.id}
                  style={{
                    padding: "12px 16px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "6px",
                    borderLeft: "3px solid #1976d2",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "14px", marginBottom: "8px" }}>{note.reasoning}</div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {note.created_by_name || "Unknown"} &middot;{" "}
                        {new Date(note.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#999",
                        cursor: "pointer",
                        fontSize: "18px",
                        padding: "0 4px",
                      }}
                      title="Delete note"
                    >
                      &times;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
